// Pure calculation functions extracted from Analytics.js.
//
// These intentionally have zero dependency on React, Firebase, or any
// component state — they take plain data in and return plain data out.
// That's what makes them unit-testable without mocking anything.

// ─── Inventory & Stock (velocity, slow movers, overstock) ──────────────────

/**
 * Aggregates raw sale/write-off logs into one entry per product.
 * Keeps the product id on each entry (the original inline version didn't,
 * which is part of what caused the overstock bug below).
 */
export function buildSalesLossMap(saleLogs, lossLogs) {
  const map = {};

  const ensureEntry = (log) => {
    if (!map[log.productId]) {
      map[log.productId] = {
        id: log.productId,
        name: log.productName,
        group: log.productGroup,
        totalSold: 0,
        totalLost: 0,
      };
    }
    return map[log.productId];
  };

  saleLogs.forEach((log) => { ensureEntry(log).totalSold += log.quantity; });
  lossLogs.forEach((log) => { ensureEntry(log).totalLost += log.quantity; });

  return map;
}

/**
 * Computes per-product sales velocity (units/day) for a given period.
 * avgPerDay is null for "all time" (days === 0), since a daily rate isn't
 * meaningful without a fixed window — matches the original UI's intent.
 */
export function computeVelocityData(salesLossMap, days) {
  const hasSpecificPeriod = days !== 0;
  return Object.values(salesLossMap)
    .filter((p) => p.totalSold > 0)
    .map((p) => ({
      ...p,
      avgPerDay: hasSpecificPeriod ? parseFloat((p.totalSold / days).toFixed(2)) : null,
    }))
    .sort((a, b) => b.totalSold - a.totalSold);
}

export function computeSlowMovers(velocityData, slowThreshold, hasSpecificPeriod) {
  return hasSpecificPeriod
    ? velocityData.filter((p) => p.avgPerDay !== null && p.avgPerDay < slowThreshold)
    : [];
}

/**
 * Flags products that are overstocked relative to their actual sales velocity.
 *
 * BUG FIX: the original inline version read velocity from the raw
 * sales/loss map, which never had `avgPerDay` attached to it (that field
 * only existed on the separate `velocityData` array). So velocity was
 * always undefined -> 0, which meant every product with stock > 0 got
 * flagged as overstocked with "Infinity days" of supply, regardless of how
 * fast it was actually selling. This version builds its velocity lookup
 * from `velocityData` (which has the real, computed avgPerDay) instead.
 */
export function computeOverstockedProducts(products, velocityData, overstockDays, hasSpecificPeriod) {
  const velocityById = {};
  velocityData.forEach((v) => { velocityById[v.id] = v.avgPerDay; });

  return products
    .filter((p) => {
      if (!hasSpecificPeriod) return false;
      const velocity = velocityById[p.id] ?? 0;
      if (velocity === 0) return p.stock > 0;
      return p.stock / velocity > overstockDays;
    })
    .map((p) => {
      const velocity = velocityById[p.id] ?? 0;
      const daysOfSupply = velocity > 0 ? Math.round(p.stock / velocity) : Infinity;
      return { ...p, velocity, daysOfSupply };
    })
    .sort((a, b) => {
      if (a.daysOfSupply === Infinity && b.daysOfSupply === Infinity) return 0;
      if (a.daysOfSupply === Infinity) return -1;
      if (b.daysOfSupply === Infinity) return 1;
      return b.daysOfSupply - a.daysOfSupply;
    });
}

// ─── Value & Sales (revenue, margin, dead stock) ────────────────────────────

/**
 * Aggregates sale logs into per-product units sold and revenue.
 * Price fallback order matches the original: an explicit price recorded on
 * the log itself, then the product's current selling price, then its cost
 * price, then zero (e.g. for historical logs with no price data at all).
 */
export function computeProductSales(saleLogs, products) {
  const map = {};
  saleLogs.forEach((log) => {
    if (!map[log.productId]) {
      map[log.productId] = {
        id: log.productId,
        name: log.productName,
        group: log.productGroup,
        totalUnits: 0,
        totalRevenue: 0,
      };
    }
    map[log.productId].totalUnits += log.quantity;

    const product = products.find((p) => p.id === log.productId);
    const price = log.salePrice ?? log.unitPrice ?? product?.sellingPrice ?? product?.costPrice ?? 0;
    map[log.productId].totalRevenue += log.quantity * price;
  });
  return map;
}

/**
 * Adds cost/selling price and margin % to each product's sales entry.
 * Margin is null (not 0) when either price is missing, so the UI can
 * distinguish "no margin data" from "zero margin".
 */
export function enrichWithMargins(productSalesMap, products) {
  return Object.values(productSalesMap).map((p) => {
    const prod = products.find((pr) => pr.id === p.id);
    const costPrice = prod?.costPrice ?? 0;
    const sellingPrice = prod?.sellingPrice ?? 0;
    const avgMargin =
      sellingPrice > 0 && costPrice > 0
        ? (((sellingPrice - costPrice) / sellingPrice) * 100).toFixed(1)
        : null;
    return { ...p, costPrice, sellingPrice, avgMargin };
  });
}

export function computeBestSellers(enriched) {
  return [...enriched].sort((a, b) => b.totalUnits - a.totalUnits);
}

export function computeLeastSellers(enriched) {
  return enriched.filter((p) => p.totalUnits > 0).sort((a, b) => a.totalUnits - b.totalUnits);
}

/** Products with stock on hand but no sales at all in the period. */
export function computeDeadStock(products, productSalesMap) {
  const soldIds = new Set(Object.keys(productSalesMap));
  return products.filter((p) => !soldIds.has(p.id) && p.stock > 0);
}

export function computeRevenueByGroup(enriched) {
  const revenueByGroup = {};
  enriched.forEach((p) => {
    revenueByGroup[p.group] = (revenueByGroup[p.group] || 0) + p.totalRevenue;
  });
  return Object.entries(revenueByGroup)
    .map(([name, value]) => ({ name, value: Math.round(value) }))
    .sort((a, b) => b.value - a.value);
}

export function computeTopRevenue(enriched) {
  return [...enriched].sort((a, b) => b.totalRevenue - a.totalRevenue);
}

export function computeTotals(enriched) {
  return {
    totalRevenue: enriched.reduce((s, p) => s + p.totalRevenue, 0),
    totalUnitsSold: enriched.reduce((s, p) => s + p.totalUnits, 0),
  };
}

/** Capital tied up in a single dead-stock product, or null if cost price is unknown. */
export function computeCapitalTiedUp(product) {
  return product.costPrice > 0 ? product.stock * product.costPrice : null;
}
